import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, MapPinIcon, ChevronDownIcon, PhoneIcon, UsersIcon, PhoneOffIcon, CalendarXIcon, TrendingDownIcon, BarChart3Icon, BuildingIcon, LockIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
const faqs = [{
  question: 'What does Viridia Analytics actually do for home service businesses?',
  answer: 'We build predictable growth engines for HVAC, painting, landscaping, and contractor businesses across Phoenix. Our core focus is speed-to-lead: we use AI-assisted automation to respond to inquiries instantly, manage targeted ad campaigns, and build conversion-focused websites to turn more traffic into booked appointments.'
}, {
  question: 'Do you help with lead follow-up after ads start running?',
  answer: 'Yes. That is our primary differentiator. We do not just generate clicks and walk away. We implement AI systems that respond to text messages, lead platform inquiries, and web forms in under 60 seconds, 24/7, ensuring leads do not go cold.'
}, {
  question: 'What is AI lead conversion?',
  answer: 'It is a system where an AI assistant handles initial inbound communication. When a lead reaches out, the AI instantly replies, answers common questions, qualifies the prospect based on your services, and can even book the estimate directly on your calendar.'
}, {
  question: 'Can you work with my current CRM?',
  answer: 'In many cases, yes. We build custom automations to connect your existing tools. If your current CRM is holding you back, we can also migrate you to a centralized command center that handles pipeline visibility, invoicing, and communications in one place.'
}, {
  question: 'Do you only work with Phoenix businesses?',
  answer: 'We are headquartered in Phoenix, Arizona, and specialize in this local market. However, we also partner with home service businesses remotely, including clients in Los Angeles and Long Beach.'
}, {
  question: 'Do you help with Yelp ads?',
  answer: 'Yes. We are a Yelp Advertising Premier Partner. We have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies to maximize your return on ad spend.'
}, {
  question: 'Can you improve how fast we respond to new leads?',
  answer: 'Absolutely. Our systems are designed to eliminate the delay between a lead reaching out and getting a response. We replace slow human follow-up with instant AI responses and missed-call text-back automation.'
}, {
  question: 'Do I need a new website to work with you?',
  answer: 'No. While we do build high-converting, mobile-first websites, they are optional conversion assets. We can often implement our AI lead conversion and ad management systems using your existing site.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-2019" data-mp="3rprqoxrwr11z">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-2020" data-mp="j8oj4dp55hns">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-2021" data-mp="a8rxkky1sywy">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-2022" data-mp="e8sd68wvvg5l" />
      </button>
      <AnimatePresence data-id="element-2023" data-mp="2z1dnm3lypejk">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2024" data-mp="2yxzp56gf5phe">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2025" data-mp="1zw0xfjcolnx">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-2026" data-mp="3o7m1rc93z2qt">
      <SEOHead title="AI Marketing Agency Phoenix | Lead Conversion for Home Services | Viridia Analytics" description="Viridia Analytics is a Phoenix AI marketing agency helping home service businesses stop losing leads with AI lead conversion, speed-to-lead automation, and ad management." canonical="/" keywords="marketing agency phoenix, AI marketing agency, AI advertising for contractors, painters, HVAC, landscapers, contractors, window installers, AI lead conversion, local ads management, home services marketing" jsonLd={LOCAL_BUSINESS_SCHEMA} faq={faqs} data-id="element-2027" data-mp="3oa8cfkrapyd9" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-2028" data-mp="1210s0r70kxd8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2029" data-mp="1703woaj311ko">
          <div className="max-w-3xl" data-id="element-2030" data-mp="1726q74jg1cu5">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-2031" data-mp="1by5eks4ruuuk">
              Stop Losing Leads You{' '}
              <span className="text-viridia" data-id="element-2032" data-mp="3mst3i73dlajm">Already Paid For</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-xl sm:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-2033" data-mp="tycifhe03jt9">
              Viridia Analytics helps home service businesses respond faster,
              follow up smarter, and turn more inbound leads into booked
              appointments - with AI-assisted automation, ad management, and
              conversion-focused systems.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-2034" data-mp="1hog96o63bp2u">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-2035" data-mp="2f5kizbs3thcd">
                See Where Your Leads Are Leaking
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2036" data-mp="1rfzmdq312lkm" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARTNER LOGOS */}
      <section className="py-8 bg-viridia-black border-t border-gray-800 overflow-hidden" data-id="element-2037" data-mp="1hl7mugpvxon7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 text-center" data-id="element-2038" data-mp="1mk10qa933hnw">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2039" data-mp="18iye9we6g5cg">
            Trusted by local service businesses
          </p>
        </div>
        <div className="relative flex overflow-x-hidden" data-id="element-2040" data-mp="1cmmn0iejp6za">
          <motion.div className="flex items-center gap-16 px-8 whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 40,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-2041" data-mp="1syzycpo1lgu2">
            {/* Quadruple the logos to ensure no gaps on ultrawide screens */}
            {[...Array(4)].map((_, i) => <Fragment key={i} data-id="element-2042" data-mp="1a39sgrczo0aw" data-mpi={i}>
                <img src="https://cdn.magicpatterns.com/uploads/oRd3jcQ42CFjrTA2feQhft/logo.jpg" alt="Gomez Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-2043" data-mp="15a6v0853c6pf" />
                <img src="https://cdn.magicpatterns.com/uploads/oKhtaguHDZQ1jwnsXbrpm4/logo.png" alt="Wall Candy Logo" className="h-20 object-contain rounded-lg" data-id="element-2044" data-mp="ve3wrddd0s7r" />
                <img src="https://cdn.magicpatterns.com/uploads/qU8ovRMu8iQdUH4ewRRymJ/logo.jpg" alt="TFP Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-2045" data-mp="2xz7wtsf5saui" />
                <img src="https://cdn.magicpatterns.com/uploads/jzSYXmHzY9XrXrg5hvwEvR/logo.jpg" alt="Nuñez Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-2046" data-mp="370xlwrlkixp4" />
                <img src="https://cdn.magicpatterns.com/uploads/fGFztLtAhzgRsKrhGD2vLx/logo-01.webp" alt="AQ Communications Logo" className="h-20 object-contain rounded-lg" data-id="element-2047" data-mp="2e95kd0a2xm4j" />
                <img src="https://cdn.magicpatterns.com/uploads/iv4wx5k6BUCRwoL6bVBSqh/Logo.png" alt="MAJ Countertops Logo" className="h-20 object-contain rounded-lg" data-id="element-2048" data-mp="2t0mwlszwpr62" />
                <img src="https://cdn.magicpatterns.com/uploads/bMUopew95CsBGQxBnkcNtY/logo.jpg" alt="Finish Coat Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-2049" data-mp="20i966egwh7h0" />
              </Fragment>)}
          </motion.div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-12 bg-viridia-surface border-y border-gray-800" data-id="element-2050" data-mp="2qogfzk1ibmdq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2051" data-mp="1t06fxala78p9">
          <ScrollReveal data-id="element-2052" data-mp="1t1zk8j8f9gu3">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" data-id="element-2053" data-mp="1xz96xr4ffee8">
              <div data-id="element-2054" data-mp="1xwwv7vp9i09m">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2055" data-mp="22xsutx2kz098">242</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2056" data-mp="22whpvtxefmgj">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-2057" data-mp="1a8lyn4qlmmr5">TFP Landscaping</p>
              </div>
              <div data-id="element-2058" data-mp="1f7ymnst7hzvb">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2059" data-mp="1f76yh31xv4f2">28</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2060" data-mp="3sjb5fxfyvtne">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-2061" data-mp="3ofbnrbiyaosg">TFP Landscaping</p>
              </div>
              <div data-id="element-2062" data-mp="3jkfygj53mfj1">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2063" data-mp="3ej8nycbkn28o">39.9%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2064" data-mp="3elcr2s1nbx7w">
                  Close Rate
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-2065" data-mp="34sxa9y235eol">TFP Landscaping</p>
              </div>
              <div data-id="element-2066" data-mp="30rlpq6y2jjn9">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2067" data-mp="30swvgcb2r1h0">138</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2068" data-mp="2vt0s8fa7be2i">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2069" data-mp="2qt61qcuc8qhz">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2070" data-mp="2c6ywx373ojhe">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2071" data-mp="2c6fnybzfh5fi">29</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2072" data-mp="2c9bjjtnszi0s">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2073" data-mp="237lw0yx1l38a">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2074" data-mp="279pa7vb3ilx9">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2075" data-mp="2uxqdaopi9a5a">$15.05k</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2076" data-mp="2uzt06zvd2n5s">
                  Closed-Won Revenue
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2077" data-mp="2m43l18kiap0u">
                  From AI-worked leads only
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 bg-viridia-black" data-id="element-2078" data-mp="3du81wx924txb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2079" data-mp="3io2tsnytgf7h">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-2080" data-mp="1782a0mfo81im">
            <div className="lg:col-span-5" data-id="element-2081" data-mp="175pjhkvukity">
              <ScrollReveal data-id="element-2082" data-mp="129r0kctaft0x">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2083" data-mp="128fw0as0qh8r">
                  Who We Partner With
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed mb-8" data-id="element-2084" data-mp="1bysdtv8g7wb7">
                  We don't work with everyone. Our systems are built
                  specifically for operators who are serious about capturing
                  demand and closing jobs.
                </p>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-7" data-id="element-2085" data-mp="y7nlzfbbo8bf">
              <div className="space-y-6" data-id="element-2086" data-mp="y5av2cnl6nm8">
                <ScrollReveal delay={0.1} data-id="element-2087" data-mp="t7ririag8kwx">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2088" data-mp="t6zlm317ef4j">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2089" data-mp="29odn4k0w29y6">
                      <BuildingIcon className="w-5 h-5 text-viridia" data-id="element-2090" data-mp="e6m118ijewa4" />
                    </div>
                    <div data-id="element-2091" data-mp="j55ml7t7y61y">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2092" data-mp="j6gsbd685nvp">
                        Local Home Service Businesses
                      </h3>
                      <p className="text-gray-400" data-id="element-2093" data-mp="n9defduz424e">
                        HVAC, plumbing, landscaping, pest control, painting,
                        roofing, window installers, and general contractors.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.2} data-id="element-2094" data-mp="16v02xzcqycqy">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2095" data-mp="16wb3lqawb4du">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2096" data-mp="1avto4kyt1dg5">
                      <TargetIcon className="w-5 h-5 text-viridia" data-id="element-2097" data-mp="1ftv95bfsjmu9" />
                    </div>
                    <div data-id="element-2098" data-mp="1ksg7147klufj">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2099" data-mp="1uz90xznot1r4">
                        Companies Investing in Growth
                      </h3>
                      <p className="text-gray-400" data-id="element-2100" data-mp="1pyblhuu2altb">
                        Businesses spending meaningfully on ads or actively
                        trying to increase their booked jobs.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.3} data-id="element-2101" data-mp="2dofobzzfehj9">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2102" data-mp="28qe7zmtdwwbh">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2103" data-mp="23ymb6ujmxucw">
                      <UsersIcon className="w-5 h-5 text-viridia" data-id="element-2104" data-mp="1zxawhk2autjh" />
                    </div>
                    <div data-id="element-2105" data-mp="2wgi7pj3gcbx7">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2106" data-mp="2whtb3hszjhns">
                        Operators Seeking Better Systems
                      </h3>
                      <p className="text-gray-400" data-id="element-2107" data-mp="pshkr152yb0p">
                        Owners who know they need better follow-up, faster
                        response times, and organized lead handling.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / SYSTEM */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2108" data-mp="705h8b2ox3oz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2109" data-mp="c0bdk4jwnede">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2110" data-mp="3t86krpyi6rrj">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2111" data-mp="3t9pzvd8fawrq">
              The Speed-to-Lead System
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2112" data-mp="23owlqup97gn">
              How we turn an inbound inquiry into a booked appointment before
              your competitor even checks their phone.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative" data-id="element-2113" data-mp="3gxnuplmff02q">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-2114" data-mp="3bzvvs7nd6dp1"></div>

            {[{
            step: '1',
            title: 'Lead Comes In',
            desc: 'A prospect finds you via targeted ads or local search and sends a message, form, or calls.'
          }, {
            step: '2',
            title: 'Instant AI Reply',
            desc: 'Within seconds, our AI assistant responds, preventing the lead from moving to the next company.'
          }, {
            step: '3',
            title: 'Qualification',
            desc: 'The AI asks specific questions to qualify the prospect based on your actual service offerings.'
          }, {
            step: '4',
            title: 'Follow-Up',
            desc: 'If they do not book immediately, automated nurture sequences keep your business top-of-mind.'
          }, {
            step: '5',
            title: 'Job Booked',
            desc: 'The qualified estimate is scheduled directly on your calendar. You just show up and close.'
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-2115" data-mp="237239f953e95" data-mpi={index}>
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full" data-id="element-2116" data-mp="1ahlhh0g0yvlw">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4 text-viridia font-bold" data-id="element-2117" data-mp="1felj8z5udc77">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-2118" data-mp="2qua67qj3ib80">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-2119" data-mp="2vvptlp8qwttk">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICE HIERARCHY */}
      <section className="py-24 bg-viridia-black" data-id="element-2120" data-mp="aff6cutdan2q">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2121" data-mp="5hwxf4fco7uq">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2122" data-mp="5hdhtud69vjv">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2123" data-mp="ispeh45wg5z">
              AI Receptionist, Ad Management & CRM Automation
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2124" data-mp="3sofpwnuyv2gp">
              The systems we build for home service businesses to capture
              demand, respond instantly, and convert it into booked jobs.
            </p>
          </ScrollReveal>

          <div className="space-y-6" data-id="element-2125" data-mp="1lwewpkw5mru4">
            {/* Primary Service */}
            <ScrollReveal data-id="element-2126" data-mp="1gwslti3stnoh">
              <Link to="/ai-lead-conversion" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 hover:border-viridia/50 transition-colors" data-id="element-2127" data-mp="1gwiwzxuhpmkd">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-2128" data-mp="23t3byyydoslu">
                  <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-2129" data-mp="1z00i0ppovh54">
                    <ZapIcon className="w-8 h-8 text-viridia" data-id="element-2130" data-mp="1u1fnn6x4brol" />
                  </div>
                  <div className="flex-grow" data-id="element-2131" data-mp="1q158p4rclqxl">
                    <div className="text-xs text-viridia font-bold tracking-wide uppercase mb-1" data-id="element-2132" data-mp="1l1ak57v1pdfp">
                      Flagship System
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-viridia transition-colors" data-id="element-2133" data-mp="1g2pq5q6dzpzp">
                      AI Lead Conversion & Automations
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-2134" data-mp="3afp47ev4cc4v">
                      Never miss a lead. We implement AI receptionists, instant
                      text-back, and automated follow-up sequences so every
                      inquiry is handled immediately.
                    </p>
                  </div>
                  <div className="hidden md:flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full bg-gray-800 group-hover:bg-viridia transition-colors" data-id="element-2135" data-mp="3hxnc5exnjf5z">
                    <ArrowRightIcon className="w-5 h-5 text-white" data-id="element-2136" data-mp="3ro880ulxy5l7" />
                  </div>
                </div>
              </Link>
            </ScrollReveal>

            {/* Secondary Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2137" data-mp="2z1dxapexekkx">
              <ScrollReveal delay={0.1} data-id="element-2138" data-mp="33zp750580v35">
                <Link to="/ad-campaigns" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2139" data-mp="33ynkrzir8m64">
                  <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-6" data-id="element-2140" data-mp="o8ubvrab71vm">
                    <TargetIcon className="w-6 h-6 text-red-500" data-id="element-2141" data-mp="3upt7079cjt42" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2142" data-mp="3pr01p247r8ez">
                    Ad Campaigns & CPC
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2143" data-mp="8gs8msgql0ii">
                    As a Yelp Advertising Premier Partner, we manage targeted ad
                    spend to drive high-intent local traffic directly into your
                    conversion system.
                  </p>
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2} data-id="element-2144" data-mp="3lp4u5fvovysr">
                <Link to="/web-design-seo" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2145" data-mp="3lqy00syhaezp">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-2146" data-mp="2t6r1rvt4h512">
                    <GlobeIcon className="w-6 h-6 text-blue-500" data-id="element-2147" data-mp="13ryhddbi9yrs" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2148" data-mp="13q59xvt2jaiq">
                    Conversion Websites
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2149" data-mp="tvlq0m3fnx07">
                    We build fast, mobile-first websites positioned as
                    conversion assets, not just digital brochures. Designed to
                    turn visitors into booked appointments.
                  </p>
                </Link>
              </ScrollReveal>
            </div>

            {/* Supporting Services */}
            <ScrollReveal delay={0.3} data-id="element-2150" data-mp="1dhi2yqqls6qc">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-2151" data-mp="1hj3arxoa5uto">
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-2152" data-mp="22mfqgdw4wy6">
                  Supporting Infrastructure
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2153" data-mp="6598qkmasnwy">
                  <Link to="/crm-business-tools" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2154" data-mp="mb2pvwn4b8kv">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2155" data-mp="hit5ew4rv0pz">
                      <BarChart3Icon className="w-5 h-5 text-gray-400" data-id="element-2156" data-mp="cm2knvi0vgy8" />
                      <h4 className="font-bold text-white" data-id="element-2157" data-mp="7nhme1mbz7cf">CRM Tools</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2158" data-mp="1595j6nmp7roh">
                      Centralized pipeline visibility and organization.
                    </p>
                  </Link>
                  <Link to="/reputation-management" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2159" data-mp="vbr68xzzhl0x">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2160" data-mp="3g29s8d3k5yca">
                      <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-2161" data-mp="3g3kw0cx0763e" />
                      <h4 className="font-bold text-white" data-id="element-2162" data-mp="3bzv87po5ksjf">Reputation</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2163" data-mp="1rjx524ouv2s5">
                      Automated review requests to build local trust.
                    </p>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2164" data-mp="20isavfnzmaux">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2165" data-mp="2a93qw0ky4ai6">
                      <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2166" data-mp="2a8u1ae3tc5d0" />
                      <h4 className="font-bold text-white" data-id="element-2167" data-mp="2f75cas5uglwt">SEO / GEO</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2168" data-mp="2jauy59v4wpe5">
                      Local search optimization for discoverability.
                    </p>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* COMPARISON: AGENCIES VS VIRIDIA */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2169" data-mp="tv3u2yhed0z5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2170" data-mp="1x6keppysoixb">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2171" data-mp="1s77qb0s9z3sm">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2172" data-mp="1s8ixzboucvp0">
              What Most Agencies Sell vs.
              <br data-id="element-2173" data-mp="26435zc317thn" />
              What We Actually Fix
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2174" data-mp="2161jsiab7e1y">
              We don't just send you clicks and hope for the best. We fix the
              operational gaps where revenue leaks out.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2175" data-mp="2b3ppg39ge5yw">
            {/* The Old Way */}
            <ScrollReveal data-id="element-2176" data-mp="2oyrtslynjn9l">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-2177" data-mp="2kutwmgsw0mj6">
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2178" data-mp="2yuleismiyn2g">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-2179" data-mp="2twa2qcco62hl">
                    <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-2180" data-mp="3dhafkywfd8ay" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-2181" data-mp="3ign21ijdwdd0">
                    Most Marketing Agencies
                  </h3>
                </div>
                <ul className="space-y-6" data-id="element-2182" data-mp="33je4u87egfdd">
                  <li className="flex items-start gap-4" data-id="element-2183" data-mp="38igzcypr7373">
                    <PhoneOffIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2184" data-mp="38gw5zcf6dqat" />
                    <div data-id="element-2185" data-mp="2upr8ogza5a3n">
                      <h4 className="font-bold text-white mb-1" data-id="element-2186" data-mp="2uphiote8j2xy">
                        Ignore Missed Calls
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2187" data-mp="2ktxrgkam8sfj">
                        They run ads, but if you're on a jobsite and miss the
                        call, that ad spend is wasted.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2188" data-mp="2fu1x7crq0fd6">
                    <TrendingDownIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2189" data-mp="2ftjrzqn2yqt4" />
                    <div data-id="element-2190" data-mp="2w89ik3ayawx9">
                      <h4 className="font-bold text-white mb-1" data-id="element-2191" data-mp="2w7htlbbuzxfy">
                        Send Low-Intent Traffic
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2192" data-mp="2mav9iorcxaez">
                        They focus on cheap clicks that clog your pipeline with
                        tire-kickers instead of serious buyers.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2193" data-mp="28k00ncguytbe">
                    <LockIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2194" data-mp="28if5plqmt8d0" />
                    <div data-id="element-2195" data-mp="23loogv33c6q0">
                      <h4 className="font-bold text-white mb-1" data-id="element-2196" data-mp="23isqx7v5nk23">
                        Long-Term Contracts
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2197" data-mp="1tmhcab83ei4h">
                        They lock you into 6 or 12 month retainers with zero
                        visibility into actual performance.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            {/* The Viridia Way */}
            <ScrollReveal delay={0.2} data-id="element-2198" data-mp="96j9ircpiudq">
              <div className="bg-viridia-black border-2 border-viridia/50 rounded-2xl p-8 h-full relative overflow-hidden" data-id="element-2199" data-mp="e3i1p4ct8r9a">
                <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-2200" data-mp="j1jn3vxpl2nx"></div>
                <div className="relative z-10" data-id="element-2201" data-mp="3hm30tktti2q5">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2202" data-mp="3mjbhti38c0pt">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-2203" data-mp="3rixwzx2mbr19">
                      <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-2204" data-mp="4ie8011aiep" />
                    </div>
                    <h3 className="text-2xl font-bold text-white" data-id="element-2205" data-mp="2yuiidbnefaqg">
                      Viridia Analytics
                    </h3>
                  </div>
                  <ul className="space-y-6" data-id="element-2206" data-mp="33r8uxozzvo74">
                    <li className="flex items-start gap-4" data-id="element-2207" data-mp="2dio9t9x55mjr">
                      <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2208" data-mp="2dh3az83snhg3" />
                      <div data-id="element-2209" data-mp="2ihhlqxws55ht">
                        <h4 className="font-bold text-white mb-1" data-id="element-2210" data-mp="24ihx3q99oqln">
                          Instant AI Follow-Up
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2211" data-mp="28j1r4kyjne3k">
                          Our AI replies to messages and missed calls in
                          seconds, booking estimates 24/7 so leads stop
                          slipping.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2212" data-mp="1kwbvq2gpahrx">
                      <BarChart3Icon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2213" data-mp="1pstxquknc20k" />
                      <div data-id="element-2214" data-mp="1btark7beb0qw">
                        <h4 className="font-bold text-white mb-1" data-id="element-2215" data-mp="1bv5e1q2bf0x3">
                          Organized Pipeline
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2216" data-mp="x73w3oa1s62z">
                          We set up a CRM that shows every lead, every job, and
                          every dollar cleanly, replacing sticky notes and
                          chaos.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2217" data-mp="125yp1j1qbb0t">
                      <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2218" data-mp="128kwliomdqj1" />
                      <div data-id="element-2219" data-mp="1g3wnfeznmwuo">
                        <h4 className="font-bold text-white mb-1" data-id="element-2220" data-mp="1b768iuzl67au">
                          Month-to-Month Partnership
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2221" data-mp="1pv9isgknw55g">
                          Flexible packages with real reporting. We sit between
                          you and the ad platforms to ensure transparency.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="py-24 bg-viridia-black border-b border-gray-800" data-id="element-2222" data-mp="wi9s8uxi1hxk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2223" data-mp="10ko7z65vg1km">
          <ScrollReveal data-id="element-2224" data-mp="10jd8vjndfhzu">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-2225" data-mp="15bd10px796lv">
              <div className="max-w-2xl" data-id="element-2226" data-mp="15d7p2d3rpeu6">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2227" data-mp="1ab95srdq122h">
                  Documented Results
                </h2>
                <p className="text-lg text-gray-400" data-id="element-2228" data-mp="1f7pvvr4hdqi7">
                  Real numbers from real local service businesses.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-white hover:text-gray-300 font-semibold transition-colors" data-id="element-2229" data-mp="1oainjvhks48k">
                View Proof Library
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2230" data-mp="3q1vrah7t4631" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2231" data-mp="3ijxlonsqxf53">
            <ScrollReveal delay={0.1} data-id="element-2232" data-mp="3dptcryzx6x1d">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2233" data-mp="38s9nm41oozun">
                <div data-id="element-2234" data-mp="34rhcf5994kvq">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-2235" data-mp="34rzeiiilhtbk">
                    Landscaping
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2236" data-mp="3sg8xzdsc64zj">
                    TFP Landscaping
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2237" data-mp="3nhz210ysf0cv">
                    <strong data-id="element-2238" data-mp="2h13tc48n9wda">Challenge:</strong> 4+ hour response time meant ad
                    spend was being wasted because leads went cold before the
                    owner could reply.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2239" data-mp="3tknbcwlzca7">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2240" data-mp="3r80albzubqa">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2241" data-mp="8lcaddgk2zvl">284%</p>
                      <p className="text-xs text-gray-400" data-id="element-2242" data-mp="3qzbrm6kyjbfx">More Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2243" data-mp="3v0f54mfj8vdz">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2244" data-mp="ml3t1ri0p4fx">242</p>
                      <p className="text-xs text-gray-400" data-id="element-2245" data-mp="rj5ak7zsovpa">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2246" data-mp="djx8968c57gv">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2247" data-mp="ihp9iquve5xq">28</p>
                      <p className="text-xs text-gray-400" data-id="element-2248" data-mp="2y9ctllosc3z4">AI Bookings</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2249" data-mp="338g07akeyi97">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2250" data-mp="3363c0fo2bbnp">39.9%</p>
                      <p className="text-xs text-gray-400" data-id="element-2251" data-mp="2upm7uvzhiugl">Close Rate</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2252" data-mp="34q4xk7dqd56f">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2253" data-mp="2c17g26hcfceq" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2254" data-mp="2lqqy59x8f28j">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2255" data-mp="2lrsm2ezcjj7o">
                <div data-id="element-2256" data-mp="2gypq6073jxob">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-2257" data-mp="2gy7kye2gi949">
                    Painting
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2258" data-mp="3ocjpfdhxo41q">
                    Finish Coat Painting
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2259" data-mp="3tav17trsgoml">
                    <strong data-id="element-2260" data-mp="1z33ht5xizda">Challenge:</strong> Generating strong inbound demand
                    but couldn't keep up with conversations. Leads were slipping
                    through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2261" data-mp="35gl71oeo5k7w">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2262" data-mp="3ad1xip9ccao5">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2263" data-mp="3ef5jw8xjszo7">29</p>
                      <p className="text-xs text-gray-400" data-id="element-2264" data-mp="3je9t2zkh2xej">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2265" data-mp="too8ahriha3n">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2266" data-mp="ymprr3sunbfn">
                        $15.05k
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-2267" data-mp="18u23zh2j395s">Closed-Won</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2268" data-mp="13vh4jjv3otie">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2269" data-mp="z0bnaycn830n">138</p>
                      <p className="text-xs text-gray-400" data-id="element-2270" data-mp="v0b3szaot1mq">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2271" data-mp="q3axcn9xdwv3">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2272" data-mp="l1255t4vmyu1">64%</p>
                      <p className="text-xs text-gray-400" data-id="element-2273" data-mp="g4lh0yxogkgy">Lead Growth</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2274" data-mp="c41n048ehwz1">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2275" data-mp="2ppsogc11xwaq" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2276" data-mp="2fxvbazqhgu9d">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2277" data-mp="2kxhm72iu9yf0">
          <ScrollReveal data-id="element-2278" data-mp="260qocxotcuri">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2279" data-mp="260h47qqg9htq">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2280" data-mp="1x0295znoood1">
          <ScrollReveal delay={0.2} data-id="element-2281" data-mp="1x1wrd67km2df">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2282" data-mp="21yvl3nnbo7b3">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2283" data-mp="3rfekr9unptgd" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2284" data-mp="5cw3h701j2rr">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2285" data-mp="5at43wczzxad">
          <ScrollReveal data-id="element-2286" data-mp="j5bxjiayrqgj">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-2287" data-mp="edc3u7lhltse">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2288" data-mp="t15lryy1p8e5">
              Book a real strategy call. We'll look at your ads, website, and
              follow-up process to find exactly where you are losing jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-200 transition-all duration-300" data-id="element-2289" data-mp="120k8qof070tw">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-2290" data-mp="2zu92r0yvv5cf" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}